/**
 * Customer News — the trade press, with what we ship beside it.
 *
 * The page is an index and a feed. The index is the companies in the news,
 * biggest customer first; clicking one narrows the feed to it. The feed is one
 * card per article, most recent first, each carrying the model's one-sentence
 * summary and a link out.
 *
 * **Everything here counts articles, never events.** Three outlets covered the
 * same Starbucks closure on one day and the feed shows three cards. The stage
 * that merges coverage into one development needs article bodies and is
 * unbuilt, so rather than pretend, a card that looks like a second write-up of
 * a story already shown says so — same company, same category, same day. That
 * badge is a labelled guess and it never changes a count.
 *
 * Nothing is computed here. Every figure, including the gallons, arrives in the
 * payload where `derive` owns it.
 */

import { useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { count, dataset, text } from '../shared/rows'

import '../report.css'

const NONE = '—'

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function gal(value: number | null): string {
  return value === null ? NONE : Math.round(value).toLocaleString('en-US')
}

/** `menu_change` reads badly in a chip; the underscores are the model's. */
function pretty(code: string | null): string {
  return code === null ? NONE : code.replace(/_/g, ' ')
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <article className="cn-kpi">
      <p className="cn-kpi-label">{label}</p>
      <p className="cn-kpi-value cell-number">{value}</p>
      {detail !== undefined && <p className="cn-kpi-detail">{detail}</p>}
    </article>
  )
}

export function CustomerNews({ result }: CustomReportProps) {
  const headline = dataset(result, 'headline')[0]
  const companies = dataset(result, 'companies_seen')
  const stories = dataset(result, 'stories')
  const signals = dataset(result, 'signals')
  const categories = dataset(result, 'categories')
  const sources = dataset(result, 'sources')
  const runs = dataset(result, 'runs')
  const asOf = dataset(result, 'as_of')

  // Narrowing the feed to one company is a reading aid, not a filter on the
  // data -- the picker in the param bar is the one that changes what is loaded.
  const [picked, setPicked] = useState<string | null>(null)

  if (headline === undefined) {
    return <Empty label="This run carried no articles" />
  }

  const shown =
    picked === null
      ? stories
      : stories.filter((row) => (text(row['company']) ?? '').toUpperCase() === picked)

  const acuteRows = stories.filter((row) => text(row['is_acute']) === 'yes')
  const staleSources = sources.filter((row) => text(row['verdict']) !== 'ok')

  return (
    <div className="bespoke-report cn-page">
      <section className="cn-kpis">
        <Kpi
          label="Articles shown"
          value={count(headline['articles']).toLocaleString()}
          detail={`of ${count(headline['corpus']).toLocaleString()} screened`}
        />
        <Kpi
          label="Companies"
          value={count(headline['companies']).toLocaleString()}
          detail={`${count(headline['customers']).toLocaleString()} are accounts we ship`}
        />
        <Kpi
          label="Their gallons TTM"
          value={gal(num(headline['customer_gallons']))}
          detail="What the named accounts buy from us"
        />
        <Kpi
          label="Acute"
          value={count(headline['acute']).toLocaleString()}
          detail="Bankruptcy, closures, supplier change"
        />
        <Kpi
          label="Names our category"
          value={count(headline['beverage_named']).toLocaleString()}
          detail="Juice, citrus, lemonade, beverage platform"
        />
        <Kpi
          label="Newest article"
          value={text(headline['newest']) ?? NONE}
          detail="Check the discovery row below before reading a quiet feed as a quiet market"
        />
      </section>

      {acuteRows.length > 0 && (
        <section className="cn-acute">
          <p className="cn-acute-head">
            {acuteRows.length} article{acuteRows.length === 1 ? '' : 's'} on bankruptcy, closures
            or a supplier change
          </p>
          <ul className="cn-acute-list">
            {acuteRows.slice(0, 6).map((row, i) => (
              <li key={`${text(row['url']) ?? ''}-${String(i)}`}>
                <b>{text(row['company']) ?? 'the trade'}</b>
                {num(row['gallons_ttm']) === null
                  ? ''
                  : ` · ${gal(num(row['gallons_ttm']))} gal`}{' '}
                — {text(row['what_happened']) ?? text(row['title'])}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Card
        title="Companies in the news"
        subtitle="Accounts we ship first, largest by trailing-year gallons. Pick one to narrow the feed below; the article count is coverage, not a number of things that happened."
      >
        {companies.length === 0 ? (
          <Empty label="No company was named in the articles this filter shows." />
        ) : (
          <div className="cn-chips">
            <button
              type="button"
              className={picked === null ? 'cn-chip is-on' : 'cn-chip'}
              onClick={() => {
                setPicked(null)
              }}
            >
              All ({stories.length})
            </button>
            {companies.map((row) => {
              const name = text(row['company']) ?? ''
              const key = name.toUpperCase()
              const ours = text(row['is_customer']) === 'true'
              const acute = count(row['acute'])
              return (
                <button
                  key={key}
                  type="button"
                  className={[
                    'cn-chip',
                    picked === key ? 'is-on' : '',
                    ours ? 'is-ours' : '',
                    acute > 0 ? 'is-acute' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    setPicked(picked === key ? null : key)
                  }}
                  title={
                    ours
                      ? `${gal(num(row['gallons_ttm']))} gal trailing year`
                      : 'Not an account we ship'
                  }
                >
                  {name} <span className="cn-chip-n">{count(row['articles'])}</span>
                  {ours && <span className="cn-chip-gal">{gal(num(row['gallons_ttm']))}</span>}
                </button>
              )
            })}
          </div>
        )}
      </Card>

      <Card
        title={picked === null ? 'The feed' : `The feed — ${picked}`}
        subtitle="Most recent first. Each card is one article; the sentence is the model's summary of the headline and blurb, because no article body has ever been fetched."
      >
        {shown.length === 0 ? (
          <Empty label="No articles match this filter." />
        ) : (
          <ol className="cn-feed">
            {shown.map((row, index) => {
              const company = text(row['company'])
              const ours = text(row['is_customer']) === 'true'
              const acute = text(row['is_acute']) === 'yes'
              const outlets = count(row['same_story'])
              const cat = text(row['category'])
              const url = text(row['url'])
              return (
                <li
                  className={acute ? 'cn-item is-acute' : 'cn-item'}
                  key={`${url ?? ''}-${String(index)}`}
                >
                  <div className="cn-item-head">
                    <span className="cn-when">{text(row['published_at']) ?? NONE}</span>
                    {company !== null && (
                      <span className={ours ? 'cn-who is-ours' : 'cn-who'}>
                        {company}
                        {/* The press name and ours differ often enough that
                            showing both is how a reader trusts the match --
                            Cheesecake Factory is CHEESECAKE in the ERP. */}
                        {ours &&
                          text(row['our_name']) !== null &&
                          (text(row['our_name']) ?? '').toUpperCase() !==
                            company.toUpperCase() && (
                            <span className="cn-alias">as {text(row['our_name'])}</span>
                          )}
                        {ours && (
                          <span className="cn-who-gal">{gal(num(row['gallons_ttm']))} gal</span>
                        )}
                      </span>
                    )}
                    {cat !== null && (
                      <span className={acute ? 'cn-tag is-acute' : 'cn-tag'}>{pretty(cat)}</span>
                    )}
                    {text(row['company_role']) !== null &&
                      text(row['company_role']) !== 'subject' && (
                        <span className="cn-tag is-quiet">{pretty(text(row['company_role']))}</span>
                      )}
                    {text(row['beverage']) === 'direct' && (
                      <span className="cn-tag is-drink">
                        drinks
                        {text(row['beverage_kinds']) === null ||
                        text(row['beverage_kinds']) === ''
                          ? ''
                          : `: ${text(row['beverage_kinds']) ?? ''}`}
                      </span>
                    )}
                  </div>

                  <p className="cn-what">{text(row['what_happened']) ?? text(row['title'])}</p>

                  <div className="cn-item-foot">
                    {url === null ? (
                      <span className="cn-src">{text(row['source_name']) ?? NONE}</span>
                    ) : (
                      <a className="cn-src" href={url} target="_blank" rel="noreferrer">
                        {text(row['source_name']) ?? 'Read it'} ↗
                      </a>
                    )}
                    {text(row['signals']) !== null && text(row['signals']) !== '' && (
                      <span className="cn-signals">{pretty(text(row['signals']))}</span>
                    )}
                    {outlets > 1 && (
                      <span
                        className="cn-echo"
                        title="Same company, same category, same day. A heuristic standing in for the deduplication stage, which needs article bodies and is unbuilt."
                      >
                        {outlets} outlets on this story
                      </span>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </Card>

      <div className="cn-columns">
        <Card title="What the coverage is about" subtitle="Signals across the shown articles. One article can carry several.">
          {signals.length === 0 ? (
            <Empty label="No signals on these articles." />
          ) : (
            <table className="cn-table">
              <tbody>
                {signals.map((row) => (
                  <tr key={text(row['signal']) ?? ''}>
                    <td>
                      {pretty(text(row['signal']))}
                      {text(row['names_our_category']) === 'yes' && (
                        <span className="cn-tag is-drink">ours</span>
                      )}
                    </td>
                    <td className="cell-number">{count(row['articles'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Categories" subtitle="How the model classified each article.">
          {categories.length === 0 ? (
            <Empty label="No categories on these articles." />
          ) : (
            <table className="cn-table">
              <tbody>
                {categories.map((row) => (
                  <tr key={text(row['category']) ?? ''}>
                    <td>
                      {pretty(text(row['category']))}
                      {text(row['is_acute']) === 'yes' && (
                        <span className="cn-tag is-acute">acute</span>
                      )}
                    </td>
                    <td className="cell-number">{count(row['articles'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card
        title="Where it comes from"
        subtitle="Ranked by silence, not by errors. A feed that errors gets noticed; one returning valid XML while publishing nothing does not, and that is the failure that actually happens."
      >
        {staleSources.length > 0 && (
          <p className="cn-note">
            {staleSources.length} of {sources.length} feeds need attention.
          </p>
        )}
        <div className="cn-scroll">
          <table className="cn-table">
            <thead>
              <tr>
                <th scope="col">Source</th>
                <th scope="col" className="cell-number">
                  Days since an item
                </th>
                <th scope="col" className="cell-number">
                  Articles 7d
                </th>
                <th scope="col" className="cell-number">
                  Failures
                </th>
                <th scope="col">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((row) => {
                const verdict = text(row['verdict']) ?? ''
                return (
                  <tr key={text(row['source_name']) ?? ''}>
                    <td>{text(row['source_name']) ?? NONE}</td>
                    <td className="cell-number">{count(row['days_since_item'])}</td>
                    <td className="cell-number">{count(row['articles_7d'])}</td>
                    <td className="cell-number">{count(row['consecutive_failures'])}</td>
                    <td className={verdict === 'ok' ? 'cn-ok' : 'cn-bad'}>{verdict}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="cn-columns">
        <Card title="Pipeline runs" subtitle="Discovery and screening, newest first. Failures are shown; a run that refused to run is information.">
          <div className="cn-scroll">
            <table className="cn-table">
              <thead>
                <tr>
                  <th scope="col">Job</th>
                  <th scope="col">Started</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="cell-number">
                    New
                  </th>
                </tr>
              </thead>
              <tbody>
                {runs.map((row, i) => (
                  <tr key={`${text(row['job']) ?? ''}-${String(i)}`}>
                    <td>{text(row['job']) ?? NONE}</td>
                    <td>{text(row['started_at']) ?? NONE}</td>
                    <td className={text(row['run_status']) === 'ok' ? 'cn-ok' : 'cn-bad'}>
                      {text(row['run_status']) ?? NONE}
                    </td>
                    <td className="cell-number">{count(row['articles_new'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="What this is as of">
          <table className="cn-table">
            <tbody>
              {asOf.map((row) => (
                <tr key={text(row['source']) ?? ''}>
                  <td className="cn-field">{text(row['source'])}</td>
                  <td>
                    <div className="cn-asof-val">{text(row['as_of'])}</div>
                    <div className="cn-note">{text(row['note'])}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  )
}
